import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd';
import { Store } from 'antd/es/form/interface';
import { buildSmartPasteFormFill } from '@/utils/smartPasteToForm';
import { buildSmartPasteGaps, type SmartPasteGap } from '@/components/SmartPaste/smartPasteGaps';
import type { BasicProduct } from '@/types/order';
import type { RegionItem } from '@/types/region';
import type { SmartPasteOrder } from '@/types/smartPaste';
import { OrderTypeEnum } from '@/stores/useOrderTypeStore';

interface UseSmartPasteFillArgs {
  form: FormInstance;
  /**
   * The form's CURRENT product list. A message that names no products leaves it
   * alone, so the gap list has to look at what the form ends up with, not at
   * what the fill happened to carry.
   */
  products: BasicProduct[];
  /** Province rows, used to resolve the area dropdown's province_code. */
  provinces?: RegionItem[] | null;
  setSelectedProvinceCode: (code?: string) => void;
  setProducts: (products: BasicProduct[]) => void;
  setOrderType: (type: OrderTypeEnum) => void;
  /**
   * Re-run the price preview. Called ONCE, after every field is in place, so
   * the request reflects the finished order and not a half-filled one.
   */
  onFilled: (allValues: Store, products: BasicProduct[]) => void;
}

/**
 * Apply a Smart Paste result to an order form. Shared by Create and Edit so the
 * two can't drift — the differences that matter are already handled by
 * buildSmartPasteFormFill, which only returns fields the parser actually found.
 *
 * Nothing is created or saved here: this pre-fills, the merchant reviews and
 * submits the form as usual.
 */
export function useSmartPasteFill({
  form,
  products,
  provinces,
  setSelectedProvinceCode,
  setProducts,
  setOrderType,
  onFilled,
}: UseSmartPasteFillArgs) {
  const { t } = useTranslation();

  return useCallback(
    (order: SmartPasteOrder): SmartPasteGap[] => {
      const fill = buildSmartPasteFormFill(order, provinces);
      if (!fill) return [];

      // Province first: it drives the area query backing the region_uid select.
      // Set the state directly — the page's own handler clears region_uid,
      // which is exactly the value we just resolved.
      if (fill.provinceCode) setSelectedProvinceCode(fill.provinceCode);

      form.setFieldsValue(fill.formValues);

      if (fill.formValues.orderType) {
        setOrderType(fill.formValues.orderType as OrderTypeEnum);
      }

      // Only replace the item list when the message actually named products —
      // on an existing order, an empty list would wipe the real one.
      if (fill.products.length) setProducts(fill.products);

      // setFieldsValue does not fire onValuesChange, so nudge the preview by
      // hand, once, on the next tick when the fields have settled.
      setTimeout(() => {
        onFilled(form.getFieldsValue(), fill.products);
      }, 0);

      // Read the finished form, not the parse: that is the only place that
      // knows what the merchant is actually looking at. setFieldsValue is
      // synchronous, so the values are already in place here.
      const finalProducts = fill.products.length ? fill.products : products;
      return buildSmartPasteGaps({
        values: form.getFieldsValue(),
        products: finalProducts,
        order,
        t,
      });
    },
    [form, products, provinces, setSelectedProvinceCode, setProducts, setOrderType, onFilled, t],
  );
}
